import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import { z } from 'zod';
import { Loader2, Building2, User, Shield, CheckCircle, ArrowLeft, Flag, Store, Globe, HelpCircle } from 'lucide-react';

const forgotSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});

const signupSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  confirmPassword: z.string().min(8),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  role: z.enum(['company', 'professional', 'admin']),
  language: z.enum(['en', 'fr']),
  phone: z.string().optional(),
  country: z.string().optional(),
  isDiaspora: z.boolean().optional(),
  companyType: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot'>(
    searchParams.get('mode') === 'signup' ? 'signup' : 'login'
  );
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<'company' | 'professional' | 'admin'>('professional');
  const [language, setLanguage] = useState<'en' | 'fr'>('en');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [isDiaspora, setIsDiaspora] = useState(false);
  const [companyType, setCompanyType] = useState('');

  const { t, i18n } = useTranslation();
  const { signIn, signUp, user, role: userRole } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Redirect if already logged in
  useEffect(() => {
    if (user && userRole) {
      const path = userRole === 'company' ? '/dashboard/company' : 
                   userRole === 'professional' ? '/dashboard/professional' : 
                   '/dashboard/admin';
      navigate(path);
    }
  }, [user, userRole, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {
      loginSchema.parse({ email, password });
    } catch (err) {
      if (err instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        err.errors.forEach((e) => {
          if (e.path[0]) newErrors[e.path[0] as string] = e.message;
        });
        setErrors(newErrors);
        return;
      }
    }

    setLoading(true);
    const { error } = await signIn(email, password);
    setLoading(false);

    if (error) {
      toast({
        title: 'Error',
        description: t('auth.errors.invalidCredentials'),
        variant: 'destructive',
      });
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {
      signupSchema.parse({
        email,
        password,
        confirmPassword,
        firstName,
        lastName,
        role,
        language,
        phone: phone || undefined,
        country: country || undefined,
        isDiaspora,
        companyType: companyType || undefined,
      });
    } catch (err) {
      if (err instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        err.errors.forEach((e) => {
          if (e.path[0]) {
            const field = e.path[0] as string;
            if (field === 'confirmPassword') {
              newErrors[field] = t('auth.errors.passwordsDontMatch');
            } else if (field === 'password') {
              newErrors[field] = t('auth.errors.passwordTooShort');
            } else if (field === 'email') {
              newErrors[field] = t('auth.errors.invalidEmail');
            } else {
              newErrors[field] = e.message;
            }
          }
        });
        setErrors(newErrors);
        return;
      }
    }

    setLoading(true);
    const { error } = await signUp(email, password, {
      firstName,
      lastName,
      role,
      language,
      phone: phone || undefined,
      country: country || undefined,
      isDiaspora: role === 'professional' ? isDiaspora : undefined,
      companyType: role === 'company' ? companyType || undefined : undefined,
    });
    setLoading(false);

    if (error) {
      if (error.message.includes('already registered')) {
        toast({
          title: 'Error',
          description: t('auth.errors.emailInUse'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: t('auth.errors.genericError'),
          variant: 'destructive',
        });
      }
    } else {
      setEmailSent(true);
    }
  };

  if (emailSent) {
    return (
      <Layout showFooter={false}>
        <div className="container flex min-h-[calc(100vh-4rem)] items-center justify-center py-8">
          <Card className="w-full max-w-md">
            <CardContent className="flex flex-col items-center p-8 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
                <CheckCircle className="h-8 w-8 text-success" />
              </div>
              <h2 className="mb-2 text-2xl font-bold">{t('auth.signup.checkEmail')}</h2>
              <p className="text-muted-foreground">{t('auth.signup.confirmEmail')}</p>
              <Button
                variant="outline"
                className="mt-6"
                onClick={() => {
                  setEmailSent(false);
                  setMode('login');
                }}
              >
                {t('nav.login')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showFooter={false}>
      <div className="container flex min-h-[calc(100vh-4rem)] items-center justify-center py-8">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">
              {mode === 'login' ? t('auth.login.title') : t('auth.signup.title')}
            </CardTitle>
            <CardDescription>
              {mode === 'login' ? t('auth.login.subtitle') : t('auth.signup.subtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {mode === 'forgot' ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">{t('auth.forgotDescription', 'Enter your email and we\'ll send you a reset link.')}</p>
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">{t('auth.login.email')}</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className={errors.email ? 'border-destructive' : ''}
                  />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>
                <Button
                  className="w-full"
                  disabled={forgotLoading}
                  onClick={async () => {
                    const result = forgotSchema.safeParse({ email: forgotEmail });
                    if (!result.success) {
                      setErrors({ email: result.error.issues[0].message });
                      return;
                    }
                    setErrors({});
                    setForgotLoading(true);
                    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
                      redirectTo: window.location.origin + '/reset-password',
                    });
                    setForgotLoading(false);
                    if (error) {
                      sonnerToast.error(error.message);
                    } else {
                      sonnerToast.success(t('auth.resetLinkSent', 'Reset link sent! Check your inbox.'));
                      setTimeout(() => setMode('login'), 2000);
                    }
                  }}
                >
                  {forgotLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('auth.sendResetLink', 'Send Reset Link')}
                </Button>
                <button
                  type="button"
                  onClick={() => { setMode('login'); setErrors({}); }}
                  className="flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <ArrowLeft className="h-3 w-3" />
                  {t('auth.backToLogin', 'Back to login')}
                </button>
              </div>
            ) : (
            <form onSubmit={mode === 'login' ? handleLogin : handleSignup} className="space-y-4">
              {mode === 'signup' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">{t('auth.signup.firstName')}</Label>
                      <Input
                        id="firstName"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className={errors.firstName ? 'border-destructive' : ''}
                      />
                      {errors.firstName && (
                        <p className="text-xs text-destructive">{errors.firstName}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">{t('auth.signup.lastName')}</Label>
                      <Input
                        id="lastName"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className={errors.lastName ? 'border-destructive' : ''}
                      />
                      {errors.lastName && (
                        <p className="text-xs text-destructive">{errors.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('auth.signup.role')}</Label>
                    <RadioGroup
                      value={role}
                      onValueChange={(v) => setRole(v as 'company' | 'professional' | 'admin')}
                      className="grid grid-cols-3 gap-2"
                    >
                      <Label
                        htmlFor="role-company"
                        className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 p-3 transition-colors ${
                          role === 'company' ? 'border-primary bg-primary/5' : 'border-muted'
                        }`}
                      >
                        <RadioGroupItem value="company" id="role-company" className="sr-only" />
                        <Building2 className="h-5 w-5" />
                        <span className="text-xs font-medium">{t('auth.signup.roleCompany')}</span>
                      </Label>
                      <Label
                        htmlFor="role-professional"
                        className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 p-3 transition-colors ${
                          role === 'professional' ? 'border-primary bg-primary/5' : 'border-muted'
                        }`}
                      >
                        <RadioGroupItem value="professional" id="role-professional" className="sr-only" />
                        <User className="h-5 w-5" />
                        <span className="text-xs font-medium">{t('auth.signup.roleProfessional')}</span>
                      </Label>
                      <Label
                        htmlFor="role-admin"
                        className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 p-3 transition-colors ${
                          role === 'admin' ? 'border-primary bg-primary/5' : 'border-muted'
                        }`}
                      >
                        <RadioGroupItem value="admin" id="role-admin" className="sr-only" />
                        <Shield className="h-5 w-5" />
                        <span className="text-xs font-medium">{t('auth.signup.roleAdmin')}</span>
                      </Label>
                    </RadioGroup>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.login.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={errors.email ? 'border-destructive' : ''}
                />
                {errors.email && (
                  <p className="text-xs text-destructive">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.login.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={errors.password ? 'border-destructive' : ''}
                />
                {errors.password && (
                  <p className="text-xs text-destructive">{errors.password}</p>
                )}
              </div>

              {mode === 'login' && (
                <div className="mt-1 text-right">
                  <button
                    type="button"
                    onClick={() => { setForgotEmail(email); setMode('forgot'); setErrors({}); }}
                    className="text-xs text-muted-foreground hover:text-primary hover:underline"
                  >
                    {t('auth.forgotPassword', 'Forgot password?')}
                  </button>
                </div>
              )}

              {mode === 'signup' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">{t('auth.signup.confirmPassword')}</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={errors.confirmPassword ? 'border-destructive' : ''}
                    />
                    {errors.confirmPassword && (
                      <p className="text-xs text-destructive">{errors.confirmPassword}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="language">{t('auth.signup.language')}</Label>
                      <Select value={language} onValueChange={(v) => setLanguage(v as 'en' | 'fr')}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="fr">Français</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">{t('auth.signup.country')}</Label>
                      <Select value={country} onValueChange={setCountry}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="US">United States</SelectItem>
                          <SelectItem value="CD">DR Congo</SelectItem>
                          <SelectItem value="FR">France</SelectItem>
                          <SelectItem value="BE">Belgium</SelectItem>
                          <SelectItem value="CA">Canada</SelectItem>
                          <SelectItem value="GB">United Kingdom</SelectItem>
                          <SelectItem value="ZA">South Africa</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">{t('auth.signup.phone')}</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>

                  {role === 'professional' && (
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="diaspora"
                        checked={isDiaspora}
                        onCheckedChange={(checked) => setIsDiaspora(checked as boolean)}
                      />
                      <Label htmlFor="diaspora" className="text-sm leading-tight cursor-pointer">
                        {t('auth.signup.diaspora')}
                      </Label>
                    </div>
                  )}

                  {role === 'company' && (
                    <div className="space-y-2">
                      <Label>{t('auth.signup.companyType')}</Label>
                      <RadioGroup
                        value={companyType}
                        onValueChange={setCompanyType}
                        className="grid grid-cols-1 gap-2"
                      >
                        {[
                          { value: 'us_company', icon: Flag },
                          { value: 'congolese_sme', icon: Store },
                          { value: 'multinational', icon: Globe },
                          { value: 'other', icon: HelpCircle },
                        ].map(({ value, icon: Icon }) => (
                          <Label
                            key={value}
                            htmlFor={`ctype-${value}`}
                            className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-3 transition-colors ${
                              companyType === value ? 'border-primary bg-primary/5' : 'border-muted'
                            }`}
                          >
                            <RadioGroupItem value={value} id={`ctype-${value}`} className="sr-only" />
                            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                            <div>
                              <span className="text-sm font-medium">{t(`companyProfile.types.${value}`)}</span>
                              <p className="text-xs text-muted-foreground">{t(`companyProfile.types.${value}_desc`)}</p>
                            </div>
                          </Label>
                        ))}
                      </RadioGroup>
                    </div>
                  )}
                </>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {mode === 'login' ? t('auth.login.button') : t('auth.signup.button')}
              </Button>
            </form>
            )}

            {mode !== 'forgot' && (
            <div className="mt-6 text-center text-sm">
              {mode === 'login' ? (
                <>
                  {t('auth.login.noAccount')}{' '}
                  <button
                    type="button"
                    onClick={() => setMode('signup')}
                    className="font-medium text-primary hover:underline"
                  >
                    {t('auth.login.signupLink')}
                  </button>
                </>
              ) : (
                <>
                  {t('auth.signup.hasAccount')}{' '}
                  <button
                    type="button"
                    onClick={() => setMode('login')}
                    className="font-medium text-primary hover:underline"
                  >
                    {t('auth.signup.loginLink')}
                  </button>
                </>
              )}
            </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}